// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputNumber } from 'antd';

// Models
import { OrderPaymentChannel } from '@/models/orders.model';

// Components
import { Modal, ModalSize } from '@/components';
import { Button, Select } from '@/ui';

// Helpers
import { getPaymentChannelOptions } from '../../helpers';

interface RecordAdditionalPaymentModalProps {
  isOpen: boolean;
  isLoading: boolean;
  defaultAmount?: number;
  onClose: () => void;
  onSubmit: (payload: { amount: number; payment_channel: OrderPaymentChannel }) => void;
}

const RecordAdditionalPaymentModal = ({
  isOpen,
  isLoading,
  defaultAmount = 0,
  onClose,
  onSubmit,
}: RecordAdditionalPaymentModalProps) => {
  const { t } = useTranslation(['orders']);
  const channelOptions = getPaymentChannelOptions(t);
  const [amount, setAmount] = useState<number>(defaultAmount);
  const [channel, setChannel] = useState<OrderPaymentChannel>(OrderPaymentChannel.CASH);

  useEffect(() => {
    if (isOpen) {
      setAmount(defaultAmount);
      setChannel(OrderPaymentChannel.CASH);
    }
  }, [isOpen, defaultAmount]);

  const handleSubmit = () => {
    if (!amount || amount <= 0) {
      return;
    }

    onSubmit({
      amount,
      payment_channel: channel,
    });
  };

  const canSubmit = Number(amount) > 0 && !isLoading;

  return (
    <Modal visible={isOpen} onCancel={onClose} size={ModalSize.LARGE}>
      <h3>{t('orders_record_additional_payment')}</h3>
      <p>{t('orders_record_additional_payment_amount')}</p>
      <InputNumber
        style={{ width: '100%', marginBottom: 16 }}
        min={0.01}
        step={0.01}
        value={amount || null}
        onChange={(value) => setAmount(value == null ? 0 : Number(value))}
      />
      <p>{t('orders_list_payment_channel')}</p>
      <Select
        options={channelOptions}
        value={channel}
        onChange={(value) => setChannel(value as OrderPaymentChannel)}
        size="large"
        style={{ width: '100%', marginBottom: 16 }}
      />
      <Button type="primary" loading={isLoading} disabled={!canSubmit} onClick={handleSubmit}>
        {t('orders_record_additional_payment')}
      </Button>
    </Modal>
  );
};

export default RecordAdditionalPaymentModal;
